import React, { ChangeEvent, useContext, useRef } from 'react';

import { Link, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { triggerEventOnEnterKeyDown } from 'common/utils/keydown.utils';
import { notify } from 'components/common/Toast/toast.helpers';
import { validateEmail, validatePassword } from 'components/auth/authValidation.utils';
import TextInput from 'components/common/TextInput/TextInput';
import PasswordInput from 'components/common/PasswordInput/PasswordInput';
import { ApplicationRoute } from 'routes/routes.enums';
import Button from 'components/common/Button/Button';
import { RootStoreContext } from 'index';
import styles from 'components/auth/SignInPage/SignInPage.module.scss';

interface ValidateSignInFormResult {
  email?: string;
  password?: string;
}

const SignInPage = () => {
  const { authStore, intermediaryApplicationsStore } = useContext(RootStoreContext);
  const {
    authStore: { isLoading },
  } = useContext(RootStoreContext);
  const navigator = useNavigate();
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [errors, setErrors] = React.useState<ValidateSignInFormResult>({});

  const handleOnEmailBlur = () => {
    setErrors((prevState) => {
      return { ...prevState, email: validateEmail(email) };
    });
  };

  const handleSubmit = async () => {
    setErrors({ email: validateEmail(email), password: validatePassword(password) });

    if (validateEmail(email) || validatePassword(password)) {
      return;
    }

    try {
      await authStore.login({ email: email.trim(), password: password.trim() });
      await authStore.getAccount();

      const companyProfile = await intermediaryApplicationsStore.getCompanyProfile();
      const firstAuthenticatedPath = !companyProfile.masterDealerAgreementWasSigned
        ? ApplicationRoute.AccountStatus
        : ApplicationRoute.Home;

      navigator(firstAuthenticatedPath);
      authStore.setAuthenticated();
    } catch (error: any) {
      const errorMessage = error?.message || 'Unexpected error';

      notify(errorMessage, 'error');
    }
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrors((prevState) => {
      return { ...prevState, email: '' };
    });

    setEmail(event.target.value);
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrors((prevState) => {
      return { ...prevState, password: '' };
    });

    setPassword(event.target.value);
  };

  return (
    <div className={styles.main}>
      <h3 className={styles.title}>Log in to your Contractor Portal</h3>
      <div className={styles.form}>
        <div className={styles.inputs}>
          <TextInput
            className={styles.emailInput}
            labelTitle="Email"
            onChange={handleEmailChange}
            value={email}
            tabIndex={1}
            errorMessage={errors.email}
            error={!!errors.email}
            containerClassName={styles.emailInputContainer}
            onKeyDown={triggerEventOnEnterKeyDown(passwordInputRef, 'focus')}
            onBlur={handleOnEmailBlur}
          />
          <PasswordInput
            ref={passwordInputRef}
            labelTitle="Password"
            tabIndex={2}
            onChange={handlePasswordChange}
            value={password}
            errorMessage={errors.password}
            containerClassName={styles.passwordInputContainer}
            onKeyDown={triggerEventOnEnterKeyDown(submitButtonRef, 'click')}
          />
        </div>
        <div className={styles.buttonsLayout}>
          <Button
            isLoading={isLoading}
            className={styles.button}
            ref={submitButtonRef}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            Log In
          </Button>
          <Link className={styles.link} to={ApplicationRoute.RequestResetPassword}>
            Forgot Password?
          </Link>
        </div>
      </div>
    </div>
  );
};

export default observer(SignInPage);
